import { useEffect, useState } from 'react'

const PERFORMANCE_QUERY_KEY = 'flipbookPerf'
const MAX_SAMPLES = 512
const MAX_PAGE_TURN_TRACES = 64

type FlipbookPerformanceSurface = 'reader' | 'stage' | 'annotation' | 'progress'
type FlipbookPerformanceInteraction = 'pan' | 'draw' | 'text'
export type FlipbookPageRequestKind = 'visible' | 'prefetch' | 'preview'

interface PageTurnIntent {
  readonly cachedPages: readonly number[]
  readonly displayPage: number
  readonly targetPage: number
  readonly targetPages: readonly number[]
}

interface PageTurnTrace {
  readonly animationDurationMs: number | null
  readonly animationEndedAtMs: number | null
  readonly compositorFrameCost: SampleSummary
  readonly decodeCompletedAtMs: number | null
  readonly decodeOrCommitWaitMs: number | null
  readonly displayPage: number
  readonly droppedFrames: number
  readonly firstRafAtMs: number | null
  readonly id: number
  readonly intentAtMs: number
  readonly longTaskCount: number
  readonly objectUrlCount: number
  readonly objectUrlCreatedAtMs: number | null
  readonly resourceWaitMs: number | null
  readonly targetCacheHit: boolean
  readonly targetPage: number
  readonly targetPages: readonly number[]
  readonly visibleCommitAtMs: number | null
  readonly visibleRequestEndAtMs: number | null
  readonly visibleRequestStartAtMs: number | null
  readonly animationStartedAtMs: number | null
  readonly animationStarted: boolean
  readonly completed: boolean
  readonly renewalCount: number
}

interface SampleSummary {
  readonly count: number
  readonly maxMs: number
  readonly p50Ms: number
  readonly p95Ms: number
  readonly p99Ms: number
}

interface PrefetchWindowSnapshot {
  readonly cacheLimit: number
  readonly cancelled: number
  readonly completed: number
  readonly constrained: boolean
  readonly queueDepth: number
  readonly scheduled: number
  readonly windowSize: number
}

export interface FirstOpenSnapshot {
  readonly completed: boolean
  readonly failed: boolean
  readonly firstVisibleCommitAtMs: number | null
  readonly imageDecodeCompletedAtMs: number | null
  readonly navigationToReaderMountMs: number | null
  readonly objectUrlCreatedAtMs: number | null
  readonly pageCount: number | null
  readonly readerMountedAtMs: number | null
  readonly readerMountToFirstVisibleCommitMs: number | null
  readonly sessionCompletedAtMs: number | null
  readonly sessionMs: number | null
  readonly sessionStartedAtMs: number | null
  readonly totalMs: number | null
  readonly visibleRequestCompletedAtMs: number | null
  readonly visibleRequestMs: number | null
  readonly visibleRequestStartedAtMs: number | null
}

export interface FlipbookPerformanceSnapshot {
  readonly enabled: boolean
  readonly durationMs: number
  readonly firstOpen: FirstOpenSnapshot
  readonly renderCommits: Readonly<Record<FlipbookPerformanceSurface, number>>
  readonly frames: SampleSummary & { readonly droppedFrames: number }
  readonly longTasks: SampleSummary
  readonly pointerToPaint: Readonly<Record<FlipbookPerformanceInteraction, SampleSummary>>
  readonly pageRequests: Readonly<Record<FlipbookPageRequestKind, { readonly cacheHits: number; readonly cancelled: number; readonly completed: number; readonly deduplicated: number; readonly failed: number; readonly started: number }>>
  readonly pageImagesLoaded: number
  readonly pageTurns: {
    readonly completed: number
    readonly compositorFrameCost: SampleSummary
    readonly decodeOrCommitWait: SampleSummary
    readonly durations: SampleSummary
    readonly droppedFrames: number
    readonly resourceWait: SampleSummary
    readonly started: number
    readonly targetCache: { readonly hits: number; readonly misses: number }
    readonly traces: readonly PageTurnTrace[]
  }
  readonly cache: { readonly hits: number; readonly mutations: number; readonly size: number }
  readonly objectUrls: { readonly created: number; readonly revoked: number }
  readonly prefetchWindow: PrefetchWindowSnapshot
  readonly progressPreviews: { readonly cancelled: number; readonly completed: number; readonly requested: number }
}

interface FlipbookPerformanceWindowApi {
  readonly getSnapshot: () => FlipbookPerformanceSnapshot
  readonly print: () => void
  readonly reset: () => void
}

declare global {
  interface Window {
    __lmsFlipbookFirstOpenNavigationStartedAt?: number
    __lmsFlipbookPerformance?: FlipbookPerformanceWindowApi
  }
}

function isDiagnosticsEnabled() {
  return typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get(PERFORMANCE_QUERY_KEY) === '1'
}

export function isFlipbookPerformanceDiagnosticsEnabled() {
  return isDiagnosticsEnabled()
}

function formatNullableMetric(value: number | null) {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.round(value)).toString()
    : 'pending'
}

export function formatFlipbookFirstOpenDiagnosticsForQa(firstOpen: FirstOpenSnapshot) {
  return [
    'Flipbook firstOpen diagnostics',
    `completed=${firstOpen.completed ? '1' : '0'}`,
    `failed=${firstOpen.failed ? '1' : '0'}`,
    `pageCount=${firstOpen.pageCount ?? 'pending'}`,
    `totalMs=${formatNullableMetric(firstOpen.totalMs)}`,
    `navigationToReaderMountMs=${formatNullableMetric(firstOpen.navigationToReaderMountMs)}`,
    `sessionMs=${formatNullableMetric(firstOpen.sessionMs)}`,
    `visibleRequestMs=${formatNullableMetric(firstOpen.visibleRequestMs)}`,
    `readerMountToFirstVisibleCommitMs=${formatNullableMetric(firstOpen.readerMountToFirstVisibleCommitMs)}`,
    `sessionStartedAtMs=${formatNullableMetric(firstOpen.sessionStartedAtMs)}`,
    `sessionCompletedAtMs=${formatNullableMetric(firstOpen.sessionCompletedAtMs)}`,
    `visibleRequestStartedAtMs=${formatNullableMetric(firstOpen.visibleRequestStartedAtMs)}`,
    `visibleRequestCompletedAtMs=${formatNullableMetric(firstOpen.visibleRequestCompletedAtMs)}`,
    `objectUrlCreatedAtMs=${formatNullableMetric(firstOpen.objectUrlCreatedAtMs)}`,
    `imageDecodeCompletedAtMs=${formatNullableMetric(firstOpen.imageDecodeCompletedAtMs)}`,
    `firstVisibleCommitAtMs=${formatNullableMetric(firstOpen.firstVisibleCommitAtMs)}`,
  ].join(' ')
}

function createEmptySummary(): SampleSummary {
  return { count: 0, maxMs: 0, p50Ms: 0, p95Ms: 0, p99Ms: 0 }
}

function createEmptyFirstOpenSnapshot(): FirstOpenSnapshot {
  return {
    completed: false,
    failed: false,
    firstVisibleCommitAtMs: null,
    imageDecodeCompletedAtMs: null,
    navigationToReaderMountMs: null,
    objectUrlCreatedAtMs: null,
    pageCount: null,
    readerMountedAtMs: null,
    readerMountToFirstVisibleCommitMs: null,
    sessionCompletedAtMs: null,
    sessionMs: null,
    sessionStartedAtMs: null,
    totalMs: null,
    visibleRequestCompletedAtMs: null,
    visibleRequestMs: null,
    visibleRequestStartedAtMs: null,
  }
}

function summarize(samples: readonly number[]): SampleSummary {
  if (samples.length === 0) {
    return createEmptySummary()
  }

  const sorted = [...samples].sort((left, right) => left - right)
  const percentile = (ratio: number) => sorted[Math.min(sorted.length - 1, Math.ceil(sorted.length * ratio) - 1)] ?? 0

  return {
    count: samples.length,
    maxMs: Number((sorted[sorted.length - 1] ?? 0).toFixed(2)),
    p50Ms: Number(percentile(0.5).toFixed(2)),
    p95Ms: Number(percentile(0.95).toFixed(2)),
    p99Ms: Number(percentile(0.99).toFixed(2)),
  }
}

function pushSample(samples: number[], value: number) {
  samples.push(value)
  if (samples.length > MAX_SAMPLES) {
    samples.shift()
  }
}

interface PageRequestMeasurement {
  readonly kind: FlipbookPageRequestKind
  readonly page: number
  readonly startedAt: number
  endedAt: number | null
  objectUrlCreatedAt: number | null
}

interface ActivePageTurn {
  readonly cachedPages: ReadonlySet<number>
  readonly displayPage: number
  readonly id: number
  readonly intentAt: number
  readonly pendingResourcePages: Set<number>
  readonly targetPage: number
  readonly targetPages: readonly number[]
  animationStartedAt: number | null
  objectUrlCreatedAt: number | null
  decodeCompletedAt: number | null
  firstRafAt: number | null
  objectUrlCount: number
  renewalCount: number
  visibleCommitAt: number | null
  visibleRequestEndAt: number | null
  visibleRequestStartAt: number | null
}

const noopDiagnostics: FlipbookPerformanceDiagnostics = {
  endPageRequest: () => undefined,
  endPointer: () => undefined,
  getSnapshot: () => ({
    cache: { hits: 0, mutations: 0, size: 0 },
    durationMs: 0,
    enabled: false,
    firstOpen: createEmptyFirstOpenSnapshot(),
    frames: { ...createEmptySummary(), droppedFrames: 0 },
    longTasks: createEmptySummary(),
    objectUrls: { created: 0, revoked: 0 },
    pageImagesLoaded: 0,
    pageRequests: {
      prefetch: { cacheHits: 0, cancelled: 0, completed: 0, deduplicated: 0, failed: 0, started: 0 },
      preview: { cacheHits: 0, cancelled: 0, completed: 0, deduplicated: 0, failed: 0, started: 0 },
      visible: { cacheHits: 0, cancelled: 0, completed: 0, deduplicated: 0, failed: 0, started: 0 },
    },
    pageTurns: {
      completed: 0,
      compositorFrameCost: createEmptySummary(),
      decodeOrCommitWait: createEmptySummary(),
      durations: createEmptySummary(),
      droppedFrames: 0,
      resourceWait: createEmptySummary(),
      started: 0,
      targetCache: { hits: 0, misses: 0 },
      traces: [],
    },
    pointerToPaint: { draw: createEmptySummary(), pan: createEmptySummary(), text: createEmptySummary() },
    prefetchWindow: { cacheLimit: 0, cancelled: 0, completed: 0, constrained: false, queueDepth: 0, scheduled: 0, windowSize: 0 },
    progressPreviews: { cancelled: 0, completed: 0, requested: 0 },
    renderCommits: { annotation: 0, progress: 0, reader: 0, stage: 0 },
  }),
  markCacheHit: () => undefined,
  markCacheMutation: () => undefined,
  markFirstOpenImageDecoded: () => undefined,
  markFirstOpenObjectUrlCreated: () => undefined,
  markFirstOpenReaderMounted: () => undefined,
  markFirstOpenSessionEnd: () => undefined,
  markFirstOpenSessionStart: () => undefined,
  markFirstOpenVisibleCommit: () => undefined,
  markFirstOpenVisibleRequestEnd: () => undefined,
  markFirstOpenVisibleRequestStart: () => undefined,
  markImageLoaded: () => false,
  markObjectUrlCreated: () => undefined,
  markObjectUrlRevoked: () => undefined,
  markPageRequestCacheHit: () => undefined,
  markPageRequestCancelled: () => undefined,
  markPageRequestDeduplicated: () => undefined,
  markPageTurnEnd: () => undefined,
  markPageTurnAnimationStart: () => undefined,
  markPageTurnIntent: () => undefined,
  markPageTurnVisibleCommit: () => undefined,
  markPageTurnVisibleFirstRaf: () => undefined,
  markPrefetchWindowCancelled: () => undefined,
  markPrefetchWindowCompleted: () => undefined,
  markPrefetchWindowScheduled: () => undefined,
  markSessionRenewal: () => undefined,
  markPreviewCancelled: () => undefined,
  markPreviewCompleted: () => undefined,
  markPreviewRequested: () => undefined,
  markRenderCommit: () => undefined,
  markPointerMove: () => undefined,
  markPointerDown: () => undefined,
  reset: () => undefined,
  start: () => undefined,
  stop: () => undefined,
  startPageRequest: () => '',
}

interface FlipbookPerformanceDiagnostics {
  readonly endPageRequest: (requestId: string, kind: FlipbookPageRequestKind, succeeded: boolean) => void
  readonly endPointer: (interaction: FlipbookPerformanceInteraction) => void
  readonly getSnapshot: () => FlipbookPerformanceSnapshot
  readonly markCacheHit: () => void
  readonly markCacheMutation: (size: number) => void
  readonly markFirstOpenImageDecoded: () => void
  readonly markFirstOpenObjectUrlCreated: () => void
  readonly markFirstOpenReaderMounted: () => void
  readonly markFirstOpenSessionEnd: (succeeded: boolean, pageCount?: number) => void
  readonly markFirstOpenSessionStart: () => void
  readonly markFirstOpenVisibleCommit: () => void
  readonly markFirstOpenVisibleRequestEnd: (succeeded: boolean) => void
  readonly markFirstOpenVisibleRequestStart: () => void
  readonly markImageLoaded: (page: number) => boolean
  readonly markObjectUrlCreated: (requestId?: string) => void
  readonly markObjectUrlRevoked: () => void
  readonly markPageRequestCacheHit: (kind: FlipbookPageRequestKind) => void
  readonly markPageRequestCancelled: (kind: FlipbookPageRequestKind) => void
  readonly markPageRequestDeduplicated: (kind: FlipbookPageRequestKind) => void
  readonly markPageTurnEnd: () => void
  readonly markPageTurnAnimationStart: () => void
  readonly markPageTurnIntent: (intent: PageTurnIntent) => void
  readonly markPageTurnVisibleCommit: (page: number) => void
  readonly markPageTurnVisibleFirstRaf: (page: number) => void
  readonly markPrefetchWindowCancelled: () => void
  readonly markPrefetchWindowCompleted: () => void
  readonly markPrefetchWindowScheduled: (windowSize: number, queueDepth: number, cacheLimit: number, constrained: boolean) => void
  readonly markSessionRenewal: () => void
  readonly markPreviewCancelled: () => void
  readonly markPreviewCompleted: () => void
  readonly markPreviewRequested: () => void
  readonly markRenderCommit: (surface: FlipbookPerformanceSurface) => void
  readonly markPointerMove: (interaction: FlipbookPerformanceInteraction) => void
  readonly markPointerDown: (interaction: FlipbookPerformanceInteraction) => void
  readonly reset: () => void
  readonly start: () => void
  readonly startPageRequest: (page: number, kind: FlipbookPageRequestKind) => string
  readonly stop: () => void
}

function createDiagnostics(): FlipbookPerformanceDiagnostics {
  if (!isDiagnosticsEnabled()) {
    return noopDiagnostics
  }

  let startedAt = 0
  let running = false
  let frameHandle: number | null = null
  let lastFrameAt: number | null = null
  let requestSequence = 0
  let firstOpenStartedAt = typeof window.__lmsFlipbookFirstOpenNavigationStartedAt === 'number'
    ? window.__lmsFlipbookFirstOpenNavigationStartedAt
    : performance.now()
  let firstOpenReaderMountedAt: number | null = null
  let firstOpenSessionStartedAt: number | null = null
  let firstOpenSessionCompletedAt: number | null = null
  let firstOpenSessionSucceeded: boolean | null = null
  let firstOpenPageCount: number | null = null
  let firstOpenVisibleRequestStartedAt: number | null = null
  let firstOpenVisibleRequestCompletedAt: number | null = null
  let firstOpenVisibleRequestSucceeded: boolean | null = null
  let firstOpenObjectUrlCreatedAt: number | null = null
  let firstOpenImageDecodeCompletedAt: number | null = null
  let firstOpenVisibleCommitAt: number | null = null
  let pageTurnStartedAt: number | null = null
  let pageTurnStarted = 0
  let pageTurnCompleted = 0
  let pageTurnSequence = 0
  let pageTurnCacheHits = 0
  let pageTurnCacheMisses = 0
  let pageTurnDroppedFrames = 0
  let cacheSize = 0
  let cacheHits = 0
  let cacheMutations = 0
  let pageImagesLoaded = 0
  let objectUrlsCreated = 0
  let objectUrlsRevoked = 0
  let previewRequested = 0
  let previewCompleted = 0
  let previewCancelled = 0
  let prefetchWindowScheduled = 0
  let prefetchWindowCompleted = 0
  let prefetchWindowCancelled = 0
  let prefetchWindowSize = 0
  let prefetchQueueDepth = 0
  let prefetchCacheLimit = 0
  let prefetchConstrained = false
  const renderCommits: Record<FlipbookPerformanceSurface, number> = { annotation: 0, progress: 0, reader: 0, stage: 0 }
  const frameSamples: number[] = []
  const longTaskSamples: number[] = []
  const pageTurnSamples: number[] = []
  const pageTurnResourceWaitSamples: number[] = []
  const pageTurnDecodeOrCommitWaitSamples: number[] = []
  const pageTurnCompositorSamples: number[] = []
  const pageTurnTraces: PageTurnTrace[] = []
  const frameEvents: Array<{ readonly timestamp: number; readonly duration: number }> = []
  const longTaskEvents: Array<{ readonly timestamp: number; readonly duration: number }> = []
  const pageRequestMeasurements = new Map<string, PageRequestMeasurement>()
  let activePageTurn: ActivePageTurn | null = null
  const pointerSamples: Record<FlipbookPerformanceInteraction, number[]> = { draw: [], pan: [], text: [] }
  const pointerStartedAt: Partial<Record<FlipbookPerformanceInteraction, number>> = {}
  const pointerFrameHandles: Partial<Record<FlipbookPerformanceInteraction, number>> = {}
  const pageRequests: Record<FlipbookPageRequestKind, { cacheHits: number; cancelled: number; completed: number; deduplicated: number; failed: number; started: number }> = {
    prefetch: { cacheHits: 0, cancelled: 0, completed: 0, deduplicated: 0, failed: 0, started: 0 },
    preview: { cacheHits: 0, cancelled: 0, completed: 0, deduplicated: 0, failed: 0, started: 0 },
    visible: { cacheHits: 0, cancelled: 0, completed: 0, deduplicated: 0, failed: 0, started: 0 },
  }

  const pageTurnDuration = () => pageTurnStartedAt === null ? undefined : performance.now() - pageTurnStartedAt
  const relativeFirstOpenTime = (timestamp: number | null) => timestamp === null
    ? null
    : Number((timestamp - firstOpenStartedAt).toFixed(2))
  const firstOpenDuration = (start: number | null, end: number | null) => start === null || end === null
    ? null
    : Number((end - start).toFixed(2))
  const getFirstOpenSnapshot = (): FirstOpenSnapshot => ({
    completed: firstOpenVisibleCommitAt !== null,
    failed: firstOpenSessionSucceeded === false || firstOpenVisibleRequestSucceeded === false,
    firstVisibleCommitAtMs: relativeFirstOpenTime(firstOpenVisibleCommitAt),
    imageDecodeCompletedAtMs: relativeFirstOpenTime(firstOpenImageDecodeCompletedAt),
    navigationToReaderMountMs: firstOpenDuration(firstOpenStartedAt, firstOpenReaderMountedAt),
    objectUrlCreatedAtMs: relativeFirstOpenTime(firstOpenObjectUrlCreatedAt),
    pageCount: firstOpenPageCount,
    readerMountedAtMs: relativeFirstOpenTime(firstOpenReaderMountedAt),
    readerMountToFirstVisibleCommitMs: firstOpenDuration(firstOpenReaderMountedAt, firstOpenVisibleCommitAt),
    sessionCompletedAtMs: relativeFirstOpenTime(firstOpenSessionCompletedAt),
    sessionMs: firstOpenDuration(firstOpenSessionStartedAt, firstOpenSessionCompletedAt),
    sessionStartedAtMs: relativeFirstOpenTime(firstOpenSessionStartedAt),
    totalMs: relativeFirstOpenTime(firstOpenVisibleCommitAt),
    visibleRequestCompletedAtMs: relativeFirstOpenTime(firstOpenVisibleRequestCompletedAt),
    visibleRequestMs: firstOpenDuration(firstOpenVisibleRequestStartedAt, firstOpenVisibleRequestCompletedAt),
    visibleRequestStartedAtMs: relativeFirstOpenTime(firstOpenVisibleRequestStartedAt),
  })
  const activePageTurnTarget = (page: number, kind: FlipbookPageRequestKind) => kind === 'visible'
    && activePageTurn?.targetPages.includes(page) === true
  const capturePageTurnTrace = (completed: boolean, endedAt: number) => {
    const turn = activePageTurn
    if (!turn) {
      return
    }

    const resourceEnd = turn.visibleRequestEndAt ?? turn.visibleCommitAt
    const resourceWait = resourceEnd === null ? null : resourceEnd - turn.intentAt
    const decodeOrCommitWait = turn.firstRafAt === null || resourceEnd === null
      ? null
      : turn.firstRafAt - resourceEnd
    const animationDuration = turn.animationStartedAt === null ? null : endedAt - turn.animationStartedAt
    const frameStart = turn.animationStartedAt ?? turn.intentAt
    const frameEventsForTurn = frameEvents.filter(({ timestamp }) => timestamp >= frameStart && timestamp <= endedAt)
    const compositorFrameCost = summarize(frameEventsForTurn.map(({ duration }) => duration))
    const droppedFrames = frameEventsForTurn.filter(({ duration }) => duration > 16.7).length

    pageTurnTraces.push({
      animationDurationMs: animationDuration === null ? null : Number(animationDuration.toFixed(2)),
      animationEndedAtMs: turn.animationStartedAt === null ? null : Number((endedAt - turn.intentAt).toFixed(2)),
      animationStarted: turn.animationStartedAt !== null,
      animationStartedAtMs: turn.animationStartedAt === null ? null : Number((turn.animationStartedAt - turn.intentAt).toFixed(2)),
      compositorFrameCost,
      completed,
      decodeCompletedAtMs: turn.decodeCompletedAt === null ? null : Number((turn.decodeCompletedAt - turn.intentAt).toFixed(2)),
      decodeOrCommitWaitMs: decodeOrCommitWait === null ? null : Number(decodeOrCommitWait.toFixed(2)),
      displayPage: turn.displayPage,
      droppedFrames,
      firstRafAtMs: turn.firstRafAt === null ? null : Number((turn.firstRafAt - turn.intentAt).toFixed(2)),
      id: turn.id,
      intentAtMs: Number(turn.intentAt.toFixed(2)),
      longTaskCount: longTaskEvents.filter(({ timestamp }) => timestamp >= frameStart && timestamp <= endedAt).length,
      objectUrlCount: turn.objectUrlCount,
      objectUrlCreatedAtMs: turn.objectUrlCreatedAt === null ? null : Number((turn.objectUrlCreatedAt - turn.intentAt).toFixed(2)),
      resourceWaitMs: resourceWait === null ? null : Number(resourceWait.toFixed(2)),
      renewalCount: turn.renewalCount,
      targetCacheHit: turn.pendingResourcePages.size === 0 && turn.cachedPages.size === turn.targetPages.length,
      targetPage: turn.targetPage,
      targetPages: [...turn.targetPages],
      visibleCommitAtMs: turn.visibleCommitAt === null ? null : Number((turn.visibleCommitAt - turn.intentAt).toFixed(2)),
      visibleRequestEndAtMs: turn.visibleRequestEndAt === null ? null : Number((turn.visibleRequestEndAt - turn.intentAt).toFixed(2)),
      visibleRequestStartAtMs: turn.visibleRequestStartAt === null ? null : Number((turn.visibleRequestStartAt - turn.intentAt).toFixed(2)),
    })
    if (pageTurnTraces.length > MAX_PAGE_TURN_TRACES) {
      pageTurnTraces.shift()
    }

    if (resourceWait !== null) {
      pushSample(pageTurnResourceWaitSamples, resourceWait)
    }
    if (decodeOrCommitWait !== null) {
      pushSample(pageTurnDecodeOrCommitWaitSamples, decodeOrCommitWait)
    }
    frameEventsForTurn.forEach(({ duration }) => pushSample(pageTurnCompositorSamples, duration))
    pageTurnDroppedFrames += droppedFrames
    activePageTurn = null
  }
  const recordFrame = (timestamp: number) => {
    if (!running) {
      return
    }

    if (lastFrameAt !== null) {
      const duration = timestamp - lastFrameAt
      pushSample(frameSamples, duration)
      frameEvents.push({ duration, timestamp })
      if (frameEvents.length > MAX_SAMPLES * 2) {
        frameEvents.shift()
      }
    }
    lastFrameAt = timestamp
    frameHandle = window.requestAnimationFrame(recordFrame)
  }
  const longTaskObserver = typeof PerformanceObserver === 'undefined'
    ? null
    : new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        pushSample(longTaskSamples, entry.duration)
        longTaskEvents.push({ duration: entry.duration, timestamp: entry.startTime })
        if (longTaskEvents.length > MAX_SAMPLES * 2) {
          longTaskEvents.shift()
        }
      })
    })

  const diagnostics: FlipbookPerformanceDiagnostics = {
    endPageRequest: (requestId, kind, succeeded) => {
      if (!requestId) {
        return
      }

      const request = pageRequests[kind]
      const measurement = pageRequestMeasurements.get(requestId)
      if (measurement) {
        measurement.endedAt = performance.now()
        if (succeeded && activePageTurnTarget(measurement.page, kind)) {
          activePageTurn!.pendingResourcePages.delete(measurement.page)
          if (activePageTurn!.pendingResourcePages.size === 0) {
            activePageTurn!.visibleRequestEndAt = measurement.endedAt
          }
        }
        if (!succeeded) {
          pageRequestMeasurements.delete(requestId)
        }
      }
      if (succeeded) {
        request.completed += 1
      } else {
        request.failed += 1
      }
      if (kind === 'visible') {
        diagnostics.markFirstOpenVisibleRequestEnd(succeeded)
      }
    },
    endPointer: (interaction) => {
      delete pointerStartedAt[interaction]
      const frameHandleForInteraction = pointerFrameHandles[interaction]
      if (frameHandleForInteraction !== undefined) {
        window.cancelAnimationFrame(frameHandleForInteraction)
        delete pointerFrameHandles[interaction]
      }
    },
    getSnapshot: () => ({
      cache: { hits: cacheHits, mutations: cacheMutations, size: cacheSize },
      durationMs: startedAt ? Number((performance.now() - startedAt).toFixed(2)) : 0,
      enabled: true,
      firstOpen: getFirstOpenSnapshot(),
      frames: {
        ...summarize(frameSamples),
        droppedFrames: frameSamples.filter((sample) => sample > 16.7).length,
      },
      longTasks: summarize(longTaskSamples),
      objectUrls: { created: objectUrlsCreated, revoked: objectUrlsRevoked },
      pageImagesLoaded,
      pageRequests,
      pageTurns: {
        completed: pageTurnCompleted,
        compositorFrameCost: summarize(pageTurnCompositorSamples),
        decodeOrCommitWait: summarize(pageTurnDecodeOrCommitWaitSamples),
        durations: summarize(pageTurnSamples),
        droppedFrames: pageTurnDroppedFrames,
        resourceWait: summarize(pageTurnResourceWaitSamples),
        started: pageTurnStarted,
        targetCache: { hits: pageTurnCacheHits, misses: pageTurnCacheMisses },
        traces: pageTurnTraces.map((trace) => ({ ...trace, targetPages: [...trace.targetPages] })),
      },
      pointerToPaint: {
        draw: summarize(pointerSamples.draw),
        pan: summarize(pointerSamples.pan),
        text: summarize(pointerSamples.text),
      },
      prefetchWindow: {
        cacheLimit: prefetchCacheLimit,
        cancelled: prefetchWindowCancelled,
        completed: prefetchWindowCompleted,
        constrained: prefetchConstrained,
        queueDepth: prefetchQueueDepth,
        scheduled: prefetchWindowScheduled,
        windowSize: prefetchWindowSize,
      },
      progressPreviews: { cancelled: previewCancelled, completed: previewCompleted, requested: previewRequested },
      renderCommits: { ...renderCommits },
    }),
    markCacheHit: () => { cacheHits += 1 },
    markCacheMutation: (size) => { cacheMutations += 1; cacheSize = size },
    markFirstOpenImageDecoded: () => {
      if (firstOpenImageDecodeCompletedAt === null) {
        firstOpenImageDecodeCompletedAt = performance.now()
      }
    },
    markFirstOpenObjectUrlCreated: () => {
      if (firstOpenObjectUrlCreatedAt === null) {
        firstOpenObjectUrlCreatedAt = performance.now()
      }
    },
    markFirstOpenReaderMounted: () => {
      if (firstOpenReaderMountedAt === null) {
        firstOpenReaderMountedAt = performance.now()
      }
    },
    markFirstOpenSessionEnd: (succeeded, pageCount) => {
      if (firstOpenSessionCompletedAt === null) {
        firstOpenSessionCompletedAt = performance.now()
        firstOpenSessionSucceeded = succeeded
        firstOpenPageCount = typeof pageCount === 'number' ? pageCount : firstOpenPageCount
      }
    },
    markFirstOpenSessionStart: () => {
      if (firstOpenSessionStartedAt === null) {
        firstOpenSessionStartedAt = performance.now()
      }
    },
    markFirstOpenVisibleCommit: () => {
      if (firstOpenVisibleCommitAt === null) {
        firstOpenVisibleCommitAt = performance.now()
      }
    },
    markFirstOpenVisibleRequestEnd: (succeeded) => {
      if (firstOpenVisibleRequestCompletedAt === null) {
        firstOpenVisibleRequestCompletedAt = performance.now()
        firstOpenVisibleRequestSucceeded = succeeded
      }
    },
    markFirstOpenVisibleRequestStart: () => {
      if (firstOpenVisibleRequestStartedAt === null) {
        firstOpenVisibleRequestStartedAt = performance.now()
      }
    },
    markImageLoaded: (page) => {
      pageImagesLoaded += 1
      if (activePageTurnTarget(page, 'visible') && activePageTurn) {
        activePageTurn.decodeCompletedAt ??= performance.now()
      }
      return true
    },
    markObjectUrlCreated: (requestId) => {
      objectUrlsCreated += 1
      const measurement = requestId ? pageRequestMeasurements.get(requestId) : undefined
      if (measurement) {
        measurement.objectUrlCreatedAt = performance.now()
        if (measurement.kind === 'visible') {
          diagnostics.markFirstOpenObjectUrlCreated()
        }
        if (activePageTurnTarget(measurement.page, measurement.kind)) {
          activePageTurn!.objectUrlCount += 1
          activePageTurn!.objectUrlCreatedAt ??= measurement.objectUrlCreatedAt
        }
      }
    },
    markObjectUrlRevoked: () => { objectUrlsRevoked += 1 },
    markPageRequestCacheHit: (kind) => { pageRequests[kind].cacheHits += 1 },
    markPageRequestCancelled: (kind) => { pageRequests[kind].cancelled += 1 },
    markPageRequestDeduplicated: (kind) => { pageRequests[kind].deduplicated += 1 },
    markPageTurnEnd: () => {
      const duration = pageTurnDuration()
      if (duration === undefined) {
        return
      }

      pushSample(pageTurnSamples, duration)
      pageTurnCompleted += 1
      capturePageTurnTrace(true, performance.now())
      pageTurnStartedAt = null
    },
    markPageTurnAnimationStart: () => {
      if (activePageTurn && activePageTurn.animationStartedAt === null) {
        activePageTurn.animationStartedAt = performance.now()
      }
    },
    markPageTurnIntent: ({ cachedPages, displayPage, targetPage, targetPages }) => {
      const now = performance.now()
      if (activePageTurn && pageTurnStartedAt !== null) {
        capturePageTurnTrace(false, now)
      }
      pageTurnStarted += 1
      pageTurnStartedAt = now
      const cachedPageSet = new Set(cachedPages)
      activePageTurn = {
        animationStartedAt: null,
        cachedPages: cachedPageSet,
        decodeCompletedAt: null,
        displayPage,
        firstRafAt: null,
        id: ++pageTurnSequence,
        intentAt: now,
        objectUrlCount: 0,
        objectUrlCreatedAt: null,
        pendingResourcePages: new Set(targetPages.filter((page) => !cachedPageSet.has(page))),
        renewalCount: 0,
        targetPage,
        targetPages: [...targetPages],
        visibleCommitAt: null,
        visibleRequestEndAt: cachedPageSet.size === targetPages.length ? now : null,
        visibleRequestStartAt: cachedPageSet.size === targetPages.length ? null : now,
      }
      if (cachedPageSet.size === targetPages.length) {
        pageTurnCacheHits += 1
      } else {
        pageTurnCacheMisses += 1
      }
    },
    markPageTurnVisibleCommit: (page) => {
      if (!activePageTurnTarget(page, 'visible') || !activePageTurn) {
        return
      }

      const now = performance.now()
      activePageTurn.visibleCommitAt ??= now
      activePageTurn.pendingResourcePages.delete(page)
      if (activePageTurn.pendingResourcePages.size === 0) {
        activePageTurn.visibleRequestEndAt ??= now
      }
    },
    markPageTurnVisibleFirstRaf: (page) => {
      if (!activePageTurnTarget(page, 'visible') || !activePageTurn) {
        return
      }

      activePageTurn.firstRafAt ??= performance.now()
      if (activePageTurn.animationStartedAt === null && pageTurnStartedAt !== null) {
        const duration = performance.now() - pageTurnStartedAt
        pushSample(pageTurnSamples, duration)
        pageTurnCompleted += 1
        capturePageTurnTrace(true, performance.now())
        pageTurnStartedAt = null
      }
    },
    markPrefetchWindowCancelled: () => {
      prefetchWindowCancelled += 1
      prefetchQueueDepth = 0
    },
    markPrefetchWindowCompleted: () => {
      prefetchWindowCompleted += 1
      prefetchQueueDepth = 0
    },
    markPrefetchWindowScheduled: (windowSize, queueDepth, cacheLimit, constrained) => {
      prefetchWindowScheduled += 1
      prefetchWindowSize = windowSize
      prefetchQueueDepth = queueDepth
      prefetchCacheLimit = cacheLimit
      prefetchConstrained = constrained
    },
    markSessionRenewal: () => {
      if (activePageTurn) {
        activePageTurn.renewalCount += 1
      }
    },
    markPreviewCancelled: () => { previewCancelled += 1 },
    markPreviewCompleted: () => { previewCompleted += 1 },
    markPreviewRequested: () => { previewRequested += 1 },
    markRenderCommit: (surface) => { renderCommits[surface] += 1 },
    markPointerMove: (interaction) => {
      const started = pointerStartedAt[interaction]
      if (started === undefined || pointerFrameHandles[interaction] !== undefined) {
        return
      }

      pointerFrameHandles[interaction] = window.requestAnimationFrame(() => {
        delete pointerFrameHandles[interaction]
        const latestStart = pointerStartedAt[interaction]
        if (latestStart !== undefined) {
          pushSample(pointerSamples[interaction], performance.now() - latestStart)
          pointerStartedAt[interaction] = performance.now()
        }
      })
    },
    markPointerDown: (interaction) => { pointerStartedAt[interaction] = performance.now() },
    reset: () => {
      Object.keys(renderCommits).forEach((surface) => { renderCommits[surface as FlipbookPerformanceSurface] = 0 })
      Object.values(pageRequests).forEach((request) => { request.cacheHits = 0; request.cancelled = 0; request.completed = 0; request.deduplicated = 0; request.failed = 0; request.started = 0 })
      frameSamples.length = 0
      frameEvents.length = 0
      longTaskSamples.length = 0
      longTaskEvents.length = 0
      pageTurnSamples.length = 0
      pageTurnResourceWaitSamples.length = 0
      pageTurnDecodeOrCommitWaitSamples.length = 0
      pageTurnCompositorSamples.length = 0
      pageTurnTraces.length = 0
      Object.values(pointerSamples).forEach((samples) => { samples.length = 0 })
      pageRequestMeasurements.clear()
      startedAt = performance.now()
      lastFrameAt = startedAt
      pageTurnStartedAt = null
      pageTurnStarted = 0
      pageTurnCompleted = 0
      pageTurnSequence = 0
      pageTurnCacheHits = 0
      pageTurnCacheMisses = 0
      pageTurnDroppedFrames = 0
      activePageTurn = null
      cacheSize = 0
      cacheHits = 0
      cacheMutations = 0
      pageImagesLoaded = 0
      objectUrlsCreated = 0
      objectUrlsRevoked = 0
      prefetchWindowScheduled = 0
      prefetchWindowCompleted = 0
      prefetchWindowCancelled = 0
      prefetchWindowSize = 0
      prefetchQueueDepth = 0
      prefetchCacheLimit = 0
      prefetchConstrained = false
      previewRequested = 0
      previewCompleted = 0
      previewCancelled = 0
      firstOpenStartedAt = typeof window.__lmsFlipbookFirstOpenNavigationStartedAt === 'number'
        ? window.__lmsFlipbookFirstOpenNavigationStartedAt
        : performance.now()
      firstOpenReaderMountedAt = null
      firstOpenSessionStartedAt = null
      firstOpenSessionCompletedAt = null
      firstOpenSessionSucceeded = null
      firstOpenPageCount = null
      firstOpenVisibleRequestStartedAt = null
      firstOpenVisibleRequestCompletedAt = null
      firstOpenVisibleRequestSucceeded = null
      firstOpenObjectUrlCreatedAt = null
      firstOpenImageDecodeCompletedAt = null
      firstOpenVisibleCommitAt = null
    },
    start: () => {
      if (running) {
        return
      }

      running = true
      diagnostics.reset()
      longTaskObserver?.observe({ entryTypes: ['longtask'] })
      frameHandle = window.requestAnimationFrame(recordFrame)
    },
    startPageRequest: (page, kind) => {
      const id = `${kind}-${page}-${++requestSequence}`
      pageRequests[kind].started += 1
      if (kind === 'visible') {
        diagnostics.markFirstOpenVisibleRequestStart()
      }
      pageRequestMeasurements.set(id, {
        endedAt: null,
        kind,
        objectUrlCreatedAt: null,
        page,
        startedAt: performance.now(),
      })
      if (kind === 'visible' && activePageTurnTarget(page, kind) && activePageTurn) {
        activePageTurn.visibleRequestStartAt ??= performance.now()
      }
      return id
    },
    stop: () => {
      running = false
      if (frameHandle !== null) {
        window.cancelAnimationFrame(frameHandle)
        frameHandle = null
      }
      longTaskObserver?.disconnect()
    },
  }

  window.__lmsFlipbookPerformance = {
    getSnapshot: diagnostics.getSnapshot,
    print: () => { console.table(diagnostics.getSnapshot()) },
    reset: diagnostics.reset,
  }

  return diagnostics
}

let diagnostics: FlipbookPerformanceDiagnostics | null = null

export function getFlipbookPerformanceDiagnostics() {
  diagnostics ??= createDiagnostics()
  return diagnostics
}

export function markFlipbookFirstOpenNavigationStart() {
  if (typeof window === 'undefined' || !isDiagnosticsEnabled()) {
    return
  }

  window.__lmsFlipbookFirstOpenNavigationStartedAt = performance.now()
}

export function useFlipbookPerformanceRun() {
  useEffect(() => {
    const activeDiagnostics = getFlipbookPerformanceDiagnostics()
    activeDiagnostics.start()
    activeDiagnostics.markFirstOpenReaderMounted()
    return () => { activeDiagnostics.stop() }
  }, [])
}

export function useFlipbookFirstOpenDiagnosticsText() {
  const [diagnosticsText, setDiagnosticsText] = useState<string | null>(null)

  useEffect(() => {
    if (!isDiagnosticsEnabled()) {
      return
    }

    let timeoutHandle: number | null = null
    let cancelled = false
    const diagnostics = getFlipbookPerformanceDiagnostics()
    const updateDiagnosticsText = () => {
      const firstOpen = diagnostics.getSnapshot().firstOpen
      setDiagnosticsText(formatFlipbookFirstOpenDiagnosticsForQa(firstOpen))
      if (!cancelled && !firstOpen.completed && !firstOpen.failed) {
        timeoutHandle = window.setTimeout(updateDiagnosticsText, 250)
      }
    }

    updateDiagnosticsText()

    return () => {
      cancelled = true
      if (timeoutHandle !== null) {
        window.clearTimeout(timeoutHandle)
      }
    }
  }, [])

  return diagnosticsText
}

export function useFlipbookPerformanceRenderCount(surface: FlipbookPerformanceSurface) {
  useEffect(() => {
    getFlipbookPerformanceDiagnostics().markRenderCommit(surface)
  })
}
